import { SITE_URL } from "@/config/pages";

export const ARTICLES = {
  "water-uit-het-plafond": {
    slug: "water-uit-het-plafond",
    readingTime: "6 minuten",
    published: "2026-09-29",
    seo: {
      title: "Water uit het plafond: wat moet u nu doen? | Spoedlekkage.nl",
      description:
        "Komt er water uit uw plafond? Doe dit direct: stroom uit, water dicht, opvangen en foto's maken. Stappenplan van de vakmensen van Spoedlekkage.nl.",
      canonical: `${SITE_URL}/kennisbank/water-uit-het-plafond`,
      index: true,
    },
    kicker: "Kennisbank — acute schade",
    title: "Water uit het plafond: wat moet u nu doen?",
    intro:
      "Komt er plotseling water uit uw plafond? Blijf rustig — met de juiste volgorde beperkt u de schade flink. Dit stappenplan is geschreven door onze vakmensen, die dit dagelijks bij woningen en bedrijfspanden tegenkomen.",
    sections: [
      {
        type: "steps",
        heading: "Doe dit direct, in deze volgorde",
        intro: "Veiligheid gaat voor alles. Werk rustig en sla geen stap over.",
        steps: [
          { title: "Schakel de stroom uit", text: "Komt water in de buurt van stopcontacten, schakelaars of verlichting? Schakel de groep van de getroffen ruimte uit in de meterkast. Bij twijfel: schakel meer groepen uit. Raak nooit natte elektrische delen aan." },
          { title: "Blijf weg bij een bolstaand plafond", text: "Blijf weg bij een plafond dat bol staat of doorzakt. Houd kinderen en huisdieren uit de ruimte en bel direct." },
          { title: "Stop de watertoevoer als dat veilig kan", text: "Sluit de hoofdkraan — die zit meestal in de meterkast of bij de watermeter. Vermoedt u een lekkende verwarmings- of cv-leiding, zet dan ook de cv-ketel uit. Lukt dit niet veilig? Laat het over aan een vakman." },
          { title: "Licht de bovenburen in", text: "Komt het water mogelijk van de bovenburen? Laat het ze meteen weten, zodat zij kunnen kijken of de kraan dicht kan." },
          { title: "Vang het water op en zet spullen droog", text: "Zet emmers en handdoeken neer en verplaats meubels, apparatuur en documenten. Elke minuut minder water scheelt later herstelwerk." },
          { title: "Maak foto's — ook van de ruimte erboven", text: "Fotografeer de vochtplek, alles wat nat is geworden én de ruimte boven het plafond (bijvoorbeeld de badkamer). Uw verzekeraar vraagt hierom, en wij kunnen op basis van deze foto's met u meekijken." },
          { title: "Bel of app een vakman", text: "Stuur uw foto's direct mee via WhatsApp — dan beoordelen wij de situatie en weet u snel waar u aan toe bent." },
        ],
      },
      {
        type: "text",
        heading: "De plek die u ziet, is zelden de plek van het lek",
        paragraphs: [
          "Water zoekt de weg van de minste weerstand. Het loopt via balken, leidingen en kieren door de constructie voordat het zichtbaar wordt. De vochtplek in uw plafond kan daarom meters verwijderd zijn van het echte lek.",
          "Daarom is het geen goed idee om zelf het plafond open te breken: grote kans dat u op de verkeerde plek breekt — met extra schade en herstelkosten als gevolg. Gericht onderzoek door een vakman voorkomt dat.",
        ],
      },
      {
        type: "list",
        heading: "De meest voorkomende oorzaken",
        intro: "In onze praktijk zien we bij water uit het plafond vrijwel altijd één van deze oorzaken:",
        items: [
          "Lekkende water- of cv-leiding in of boven het plafond",
          "Daklekkage — vaak zichtbaar tijdens of na regenval",
          "Lekkende badkamer op de verdieping erboven: voegen, kitwerk of afvoer",
          "Lekkende radiator- of vloerverwarmingsleidingen",
          "Verstopte of lekkende afvoer van bad, douche of wasmachine",
        ],
      },
      {
        type: "text",
        heading: "Wat wij doen als u belt",
        paragraphs: [
          "Op basis van uw foto's geven wij een eerste inschatting. Op locatie stellen wij de oorzaak vast — gericht, met zo min mogelijk breekwerk. Daarna bespreken we de aanpak en de kosten met u, voordat we beginnen.",
          "Waar mogelijk lossen wij het lek ter plaatse op. Lukt een definitieve reparatie niet meteen, dan beperken we eerst de schade met een tijdelijke oplossing. U ontvangt van ons een helder rapport met foto's voor uw verzekeraar — en desgewenst herstellen wij ook de gevolgschade aan plafond, muren en vloeren.",
        ],
      },
      {
        type: "list",
        heading: "Zo regelt u het slim met uw verzekering",
        intro: "Drie dingen die uw verzekeraar blij maken — en uzelf ook:",
        items: [
          "Meld de schade snel bij uw opstalverzekeraar, ook als de omvang nog onduidelijk is",
          "Maak foto's vóór u gaat opruimen of drogen — die zijn uw bewijs",
          "Bewaar bonnen van noodzakelijke kosten en vraag ons om een rapport met foto's",
        ],
      },
    ],
    cta: {
      title: "Twijfelt u waar het water vandaan komt?",
      text: "Stuur foto's van de plek en de ruimte erboven — wij kijken met u mee en geven een eerste inschatting.",
    },
    faqTitle: "Veelgestelde vragen over water uit het plafond",
    faqs: [
      { q: "Moet ik eerst de verzekering bellen of eerst een vakman?", a: "Bel eerst een vakman om de schade te beperken — elke minuut water kost later herstelwerk. Meld de schade daarna zo snel mogelijk bij uw verzekeraar." },
      { q: "Kan ik zelf het plafond openmaken om te kijken?", a: "Dat raden wij af. De bron zit zelden recht boven de plek, dus de kans is groot dat u op de verkeerde plek breekt. Gericht onderzoek door een vakman voorkomt onnodige schade." },
      { q: "Is water uit het plafond altijd spoed?", a: "Ja. Er is risico op kortsluiting, de constructie kan aangetast raken en vocht trekt verder in muren en vloeren. Ook schimmel kan zich binnen enkele dagen vormen." },
      { q: "Wordt waterschade door een lek vergoed?", a: "In veel gevallen (deels) via uw opstalverzekering. Wij leveren een helder rapport met foto's van oorzaak en herstel dat u direct bij uw verzekeraar kunt indienen." },
      { q: "Kan ik de lamp nog gebruiken als er water uit komt?", a: "Nee. Zet de groep van die ruimte uit in de meterkast en raak de lamp en schakelaar niet aan." },
      { q: "Het water komt van de bovenburen. Wie betaalt?", a: "Dat hangt af van de oorzaak. Meld de schade bij uw eigen verzekeraar en laat de buren en eventueel de VvE weten dat er water doorkomt." },
    ],
    related: [
      { to: "/lekdetectie", label: "Lekdetectie: lekkage opsporen zonder hakken en breken" },
      { to: "/daklekkage", label: "Daklekkage: hulp bij een lekkend dak" },
      { to: "/leidinglekkage", label: "Leidinglekkage" },
      { to: "/waterschade", label: "Waterschade herstel" },
      { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
      { to: "/lekkage-vve", label: "Lekkage bij VvE's" },
      { to: "/lekkage-amsterdam", label: "Lekkage in Amsterdam: 24/7 bereikbaar" },
      { to: "/lekkage-utrecht", label: "Lekkage in Utrecht: 24/7 bereikbaar" },
      { to: "/lekkage-almere", label: "Lekkage in Almere: 24/7 bereikbaar" },
    ],
  },

  "lekkage-wat-te-doen": {
    slug: "lekkage-wat-te-doen",
    readingTime: "6 minuten",
    published: "2026-10-20",
    author: "Danny Kroon",
    seo: {
      title: "Lekkage, wat te doen? 6 stappen die u nu zelf kunt doen",
      description: "Lekkage in huis? Wat nu: sluit het water af, zet de stroom veilig, beperk de schade en maak foto's. Hulp nodig? Bel of app ons 24/7.",
      canonical: `${SITE_URL}/kennisbank/lekkage-wat-te-doen`,
      index: true,
    },
    kicker: "Kennisbank — acute schade",
    title: "Lekkage, wat te doen? Het stappenplan",
    intro: "Een lekkage komt bijna altijd onverwacht: een plas onder de keukenkast, water dat langs de lamp druppelt of een natte plek die elke dag groter wordt. Wie in de eerste minuten de juiste dingen doet, beperkt de schade flink en houdt het voor de verzekering overzichtelijk. In dit stappenplan leest u wat u zelf kunt doen, in welke volgorde, en wanneer u een lekkagespecialist inschakelt.",
    summary: "Sluit de hoofdkraan af, houd water uit de buurt van elektra, beperk de schade, maak foto's, meld het bij uw verzekeraar en schakel een lekkagespecialist in.",
    sections: [
      {
        type: "text",
        heading: "1. Sluit het water af",
        paragraphs: [
          "Komt het water uit een leiding, kraan of apparaat, draai dan de hoofdkraan dicht. Die zit meestal bij de watermeter: in de meterkast, de kelder of de kruipruimte. Draai rechtsom tot hij vastzit. Lekt alleen één kraan, toilet of wasmachine, dan kunt u vaak ook het stopkraantje onder dat apparaat dichtdraaien. Komt het water van buiten of van de bovenburen, dan helpt de hoofdkraan niet; ga dan door naar stap 2.",
        ],
      },
      {
        type: "text",
        heading: "2. Zorg dat het veilig is",
        paragraphs: [
          "Water en elektriciteit gaan niet samen. Komt er water in de buurt van stopcontacten, schakelaars, lampen of de meterkast, schakel dan de betreffende groep uit. Weet u niet welke groep het is, zet dan de hoofdschakelaar om. Raak natte apparaten en snoeren niet aan en loop niet door een plas waar een stekker in ligt. Hangt het plafond bol of zakt het door, blijf dan uit die ruimte.",
        ],
      },
      {
        type: "text",
        heading: "3. Beperk de schade",
        paragraphs: [
          "Zet emmers, bakken en handdoeken neer en haal meubels, elektronica, papieren en vloerkleden weg uit de natte zone. Dweil staand water zo snel mogelijk op en zet ramen open om te ventileren. Prik een bol staand plafond nooit zelf door: er kan veel water boven zitten en het plafond kan bezwijken. Vraag eerst advies.",
        ],
      },
      {
        type: "text",
        heading: "4. Maak foto's en noteer wat er gebeurd is",
        paragraphs: [
          "Fotografeer de plek waar het water vandaan komt en alle schade, ook in de ruimte erboven. Noteer datum en tijd en wat u gedaan heeft. Bewaar kapotte onderdelen, zoals een gesprongen slang of koppeling. Uw verzekeraar vraagt daar vaak om, en het helpt de vakman bij een eerste inschatting.",
        ],
      },
      {
        type: "text",
        heading: "5. Meld de lekkage",
        paragraphs: [
          "Meld de schade zo snel mogelijk bij uw verzekeraar. Huurt u, meld het dan ook bij uw verhuurder of woningcorporatie. Woont u in een appartement, waarschuw dan de bovenburen en de VvE of beheerder, zeker als het water van boven komt.",
        ],
      },
      {
        type: "text",
        heading: "6. Laat de oorzaak opsporen en herstellen",
        paragraphs: [
          "Een natte plek zit lang niet altijd recht onder het lek: water loopt eerst over leidingen, balken en isolatie. Een lekkagespecialist zoekt de echte bron, met zo min mogelijk breekwerk, en herstelt het lek. Bij Spoedlekkage.nl krijgt u daarbij een rapport met foto's voor uw verzekeraar.",
        ],
        link: { to: "/lekdetectie", label: "Meer over lekdetectie" },
      },
      {
        type: "list",
        heading: "Wat u beter niet doet",
        items: [
          "Doorgaan met douchen, wassen of doortrekken als u niet weet waar het lek zit",
          "Zelf het dak op gaan bij regen, wind of in het donker",
          "Een bol plafond zelf doorprikken",
          "Schade al laten herstellen voordat u foto's heeft gemaakt",
        ],
      },
      {
        type: "links",
        heading: "Welke lekkage heeft u?",
        items: [
          { to: "/daklekkage", label: "Daklekkage" },
          { to: "/leidinglekkage", label: "Leidinglekkage" },
          { to: "/cv-lekkage", label: "Cv-lekkage" },
          { to: "/lekkage-badkamer", label: "Lekkage in de badkamer" },
          { to: "/kennisbank/water-uit-het-plafond", label: "Water uit het plafond" },
        ],
      },
    ],
    cta: {
      title: "Lekkage? Wij zijn 24/7 bereikbaar.",
      text: "Bel of app ons en stuur een foto — dan weet u snel waar u aan toe bent.",
    },
    faqTitle: "Veelgestelde vragen over wat te doen bij een lekkage",
    faqs: [
      { q: "Waar zit de hoofdkraan?", a: "Meestal vlak bij de watermeter: in de meterkast, de kelder of de kruipruimte. Weet u het niet? Zoek de waterleiding die uw woning binnenkomt; de kraan zit daar vlak achter." },
      { q: "Moet ik de stroom uitzetten bij een lekkage?", a: "Alleen als er water bij elektra komt of kan komen. Schakel dan de betreffende groep of de hoofdschakelaar uit." },
      { q: "Wanneer bel ik een lekkagespecialist?", a: "Zodra u de bron niet zelf kunt vinden of stoppen, of als er water uit plafond, muur of vloer komt." },
      { q: "Wordt de schade vergoed?", a: "Plotselinge schade wordt vaak vergoed door de opstal- of inboedelverzekering; slijtage en achterstallig onderhoud meestal niet. Lees meer in ons artikel over lekkage melden bij de verzekering.", link: { to: "/kennisbank/lekkage-melden-verzekering", label: "Lekkage melden bij de verzekering" } },
    ],
    related: [
      { to: "/lekdetectie", label: "Lekdetectie: lekkage opsporen" },
      { to: "/kennisbank/water-uit-het-plafond", label: "Water uit het plafond: wat nu?" },
      { to: "/kennisbank/lekkage-melden-verzekering", label: "Lekkage melden bij de verzekering" },
      { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
    ],
  },

  "lekkage-bovenburen-wie-betaalt": {
    slug: "lekkage-bovenburen-wie-betaalt",
    readingTime: "6 minuten",
    published: "2026-10-20",
    author: "Danny Kroon",
    seo: {
      title: "Lekkage bovenburen: wie betaalt? Verzekering, VvE of buren",
      description: "Lekkage door de bovenburen? Lees wie aansprakelijk is en wie betaalt: uw inboedel-, de opstalverzekering, de VvE of de buren. Met stappenplan.",
      canonical: `${SITE_URL}/kennisbank/lekkage-bovenburen-wie-betaalt`,
      index: true,
    },
    kicker: "Kennisbank — verzekering",
    title: "Lekkage van de bovenburen: wie betaalt de schade?",
    intro: "Water dat door uw plafond komt terwijl de oorzaak bij de bovenburen zit: in een appartement is dat een van de meest voorkomende lekkages. Naast de schade zelf zit u dan met de vraag wie wat betaalt. Het antwoord hangt af van wat er beschadigd is en waar het lek zit. In dit artikel leest u hoe het meestal zit en wat u het beste kunt doen.",
    summary: "Uw eigen spullen vallen meestal onder uw eigen inboedelverzekering. Schade aan het gebouw valt onder de opstalverzekering van de eigenaar of VvE. De bovenburen zijn alleen aansprakelijk als hun iets te verwijten valt.",
    sections: [
      {
        type: "text",
        heading: "Eerst: stop de lekkage",
        paragraphs: [
          "Waarschuw de bovenburen zo snel mogelijk, zodat zij de kraan of het apparaat dichtdraaien. Zijn ze niet thuis, neem dan contact op met de VvE, de beheerder of de verhuurder. Kan het water niet worden gestopt, dan kan de hoofdkraan van het gebouw dicht. Ondertussen beperkt u in uw eigen woning de schade: water opvangen, spullen weghalen, stroom uit bij water in de buurt van elektra.",
        ],
      },
      {
        type: "text",
        heading: "Uw spullen: uw eigen inboedelverzekering",
        paragraphs: [
          "Natte meubels, vloerkleden, kleding en elektronica meldt u bij uw eigen inboedelverzekering, ook als het water van de buren komt. Dat lijkt misschien vreemd, maar zo werkt het in Nederland meestal: ieder meldt de schade bij de eigen verzekeraar, en die regelt zo nodig het verhaal op een ander.",
        ],
      },
      {
        type: "text",
        heading: "Het gebouw: de opstalverzekering",
        paragraphs: [
          "Schade aan plafond, muren en vloeren valt onder de opstalverzekering van de eigenaar van het gebouw. In een appartementencomplex heeft vaak de VvE die verzekering. Huurt u, dan is het de verhuurder of woningcorporatie. Meld de schade dus ook bij de VvE of verhuurder.",
        ],
      },
      {
        type: "text",
        heading: "Wanneer betalen de bovenburen?",
        paragraphs: [
          "De bovenburen zijn niet automatisch aansprakelijk. Dat zijn ze vaak alleen als hun iets te verwijten valt, bijvoorbeeld omdat ze een bekend lek lieten zitten of een kraan open lieten staan. In dat geval kan uw verzekeraar de schade verhalen op hun aansprakelijkheidsverzekering. Ging er plotseling iets kapot, zoals een gesprongen leiding, dan betaalt ieder meestal via de eigen verzekering.",
        ],
      },
      {
        type: "text",
        heading: "De reparatie van het lek zelf",
        paragraphs: [
          "Zit het lek in een leiding, afvoer of badkamer van de bovenburen, dan is het herstel voor hun rekening of voor die van de VvE. Welke delen privé zijn en welke gemeenschappelijk, staat in de splitsingsakte en het reglement van de VvE. Leidingen in de muren en vloeren tussen appartementen zijn vaak gemeenschappelijk.",
        ],
      },
      {
        type: "steps",
        heading: "Stappenplan bij lekkage van de bovenburen",
        steps: [
          { title: "Laat het water stoppen", text: "Bovenburen, VvE, beheerder of verhuurder." },
          { title: "Maak foto's", text: "Van de schade bij u en, als dat kan, van de oorzaak bij de buren." },
          { title: "Meld uw schade", text: "Meld de schade aan uw spullen bij uw eigen inboedelverzekering." },
          { title: "Meld de gebouwschade", text: "Meld de schade aan het gebouw bij de VvE of verhuurder." },
          { title: "Laat de oorzaak vaststellen", text: "Een rapport van een lekkagespecialist helpt verzekeraars om te bepalen wie wat betaalt." },
        ],
        links: [
          { to: "/lekkage-vve", label: "Meer over lekkage bij een VvE" },
          { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
          { to: "/lekkage-badkamer", label: "Lekkage in de badkamer" },
        ],
      },
      {
        type: "text",
        heading: "Disclaimer",
        paragraphs: ["Dit is algemene informatie. Wat er precies vergoed wordt, hangt af van uw polis en de afspraken in uw VvE."],
      },
    ],
    cta: {
      title: "Water van de bovenburen?",
      text: "Bel of app ons, 24/7. Wij zoeken de bron en leggen alles vast in een rapport.",
    },
    faqTitle: "Veelgestelde vragen over lekkage van de bovenburen",
    faqs: [
      { q: "Moet ik zelf de schade melden of doen de bovenburen dat?", a: "Meld schade aan uw eigen spullen zelf bij uw eigen verzekeraar. Uw verzekeraar regelt zo nodig het verhaal." },
      { q: "Wie betaalt het plafond?", a: "Meestal de opstalverzekering van de eigenaar of de VvE." },
      { q: "Wat als de bovenburen niet thuis zijn?", a: "Neem contact op met de VvE, de beheerder of de verhuurder. In noodgevallen kan de hoofdkraan van het gebouw dicht." },
      { q: "Wat als ik geen inboedelverzekering heb?", a: "Dan kunt u de schade aan uw spullen alleen vergoed krijgen als de bovenburen aansprakelijk zijn. Bewaar foto's en bonnen en vraag bij twijfel juridisch advies." },
    ],
    related: [
      { to: "/lekkage-vve", label: "Lekkage bij een VvE" },
      { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
      { to: "/lekkage-badkamer", label: "Lekkage in de badkamer" },
      { to: "/kennisbank/water-uit-het-plafond", label: "Water uit het plafond: wat nu?" },
    ],
  },

  "lekkage-melden-verzekering": {
    slug: "lekkage-melden-verzekering",
    readingTime: "6 minuten",
    published: "2026-10-20",
    author: "Danny Kroon",
    seo: {
      title: "Lekkage melden bij de verzekering: zo doet u het | Spoedlekkage.nl",
      description: "Lekkage melden bij uw verzekeraar? Lees welke verzekering wat dekt, welke foto's en gegevens u nodig heeft en waar u op moet letten.",
      canonical: `${SITE_URL}/kennisbank/lekkage-melden-verzekering`,
      index: true,
    },
    kicker: "Kennisbank — verzekering",
    title: "Lekkage melden bij de verzekering: zo pakt u het aan",
    intro: "Na een lekkage wilt u vooral dat het lek gestopt wordt. Maar hoe u de schade meldt, bepaalt voor een groot deel of en hoeveel u vergoed krijgt. In dit artikel leest u welke verzekering wat dekt, wat u bij de melding nodig heeft en welke fouten u beter kunt vermijden.",
    summary: "Meld de schade zo snel mogelijk, beperk verdere schade, maak foto's, bewaar bonnen en kapotte onderdelen en laat de oorzaak vastleggen.",
    sections: [
      {
        type: "list",
        heading: "Welke verzekering dekt wat?",
        items: [
          "Opstalverzekering: schade aan het huis zelf, zoals plafond, vloer, muren en vaste installaties. Die heeft de eigenaar; in een appartement vaak de VvE.",
          "Inboedelverzekering: schade aan uw spullen, zoals meubels, vloerkleden, kleding en elektronica.",
          "Aansprakelijkheidsverzekering: als u schade bij een ander veroorzaakt en u daarvoor aansprakelijk bent.",
        ],
        outro: "Het repareren van het lek zelf, zoals de kapotte leiding of het lekkende dak, valt vaak niet onder de dekking. De gevolgschade wel: de natte vloer, het plafond, de spullen.",
      },
      {
        type: "text",
        heading: "Wat wordt meestal niet vergoed?",
        paragraphs: ["Schade door slijtage, achterstallig onderhoud of een lek dat al lang bestond, wordt meestal niet vergoed. Denk aan verouderde kitranden in de badkamer of een dak dat al jaren aan vervanging toe is. Plotselinge schade, zoals een gesprongen leiding of storm, meestal wel."],
      },
      {
        type: "list",
        heading: "Wat heeft u nodig bij de melding?",
        items: [
          "Foto's van de oorzaak en van alle schade",
          "Datum en tijdstip van de lekkage",
          "Een korte beschrijving van wat er gebeurde en wat u gedaan heeft",
          "Bonnen of aankoopbewijzen van beschadigde spullen",
          "De rekening of het rapport van de lekkagespecialist",
        ],
      },
      {
        type: "text",
        heading: "Schade beperken hoort erbij",
        paragraphs: ["Verzekeraars verwachten dat u redelijke maatregelen neemt om de schade te beperken: water afsluiten, opvangen, spullen weghalen. De kosten daarvan worden vaak vergoed. Een noodreparatie om erger te voorkomen mag meestal ook; maak wel eerst foto's."],
      },
      {
        type: "list",
        heading: "Veelgemaakte fouten",
        items: [
          "Te laat melden",
          "Kapotte onderdelen weggooien",
          "Alles al herstellen voordat er foto's zijn gemaakt",
          "Een lek dat al langer bestond niet vermelden",
        ],
      },
      {
        type: "text",
        heading: "Hulp van Spoedlekkage.nl",
        paragraphs: ["Wij sporen de oorzaak op en leggen die vast met foto's en een korte beschrijving. Zo kunt u uw melding onderbouwen en weet uw verzekeraar snel wat er aan de hand is. Daarna herstellen we het lek en de schade, zodat u voor alles één aanspreekpunt heeft."],
        links: [
          { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
          { to: "/lekdetectie", label: "Meer over lekdetectie" },
        ],
      },
      {
        type: "text",
        heading: "Disclaimer",
        paragraphs: ["Dit is algemene informatie; de voorwaarden van uw polis zijn leidend."],
      },
    ],
    cta: {
      title: "Lekkage en hulp nodig bij de melding?",
      text: "Bel of app ons, 24/7. Wij leveren het rapport met foto's voor uw verzekeraar.",
    },
    faqTitle: "Veelgestelde vragen over lekkage melden",
    faqs: [
      { q: "Hoe snel moet ik een lekkage melden?", a: "Zo snel mogelijk. Kijk in uw polisvoorwaarden voor de precieze termijn." },
      { q: "Worden de kosten van lekdetectie vergoed?", a: "Bij veel verzekeraars wel, als er sprake is van gedekte schade. Vraag het na bij uw verzekeraar." },
      { q: "Mag ik al laten repareren voordat de expert is geweest?", a: "Noodreparaties om schade te beperken mogen meestal; maak eerst foto's en bewaar onderdelen. Overleg bij twijfel met uw verzekeraar." },
      { q: "Wie meldt de schade in een appartement?", a: "Schade aan uw spullen meldt u bij uw eigen inboedelverzekering. Schade aan het gebouw meldt u bij de VvE of verhuurder, die het bij de opstalverzekering meldt." },
    ],
    related: [
      { to: "/lekkage-verzekering", label: "Lekkage en verzekering" },
      { to: "/lekdetectie", label: "Lekdetectie" },
      { to: "/kennisbank/lekkage-bovenburen-wie-betaalt", label: "Lekkage van de bovenburen: wie betaalt?" },
      { to: "/kennisbank/lekkage-wat-te-doen", label: "Lekkage, wat te doen? Het stappenplan" },
    ],
  },
};
